import { c as _c } from "react/compiler-runtime";
import type { NextRouter } from 'next/router';
import { useContext, useEffect, useMemo } from 'react';
import { RouterContext } from './contexts/router';
import { pushRoute, replaceRoute, createLink, routeToString, createClickHandler, findParams } from './helpers';
import type { Route, RouteOptions } from './types';
export interface RouterHook {
  router: NextRouter;
  pushRoute: (route: Route, options?: RouteOptions) => ReturnType<typeof pushRoute>;
  replaceRoute: (route: Route, options?: RouteOptions) => ReturnType<typeof replaceRoute>;
  createLink: (route: Route) => ReturnType<typeof createLink>;
  createHref: (route: Route) => string;
  createClickHandler: (route: Route) => ReturnType<typeof createClickHandler>;
  isRouteActive: (route: Route) => boolean;
  getQuery<T>(key: string): T;
}

/**
 * Get a value from the router.query and set it's type.
 * @param router Next router
 * @param key Query value
 */
function getQuery<T>(router: NextRouter, key: string): T {
  return router.query[key] as unknown as T;
}

/**
 * A replacement for useRouter from Next.js. This returns a modified API that accepts Route objects and some helper
 * functions for generating links, click handlers, and hrefs.
 */
export function useRouter() {
  const $ = _c(2);
  const router = useContext(RouterContext);
  if (!router) {
    throw new Error("Missing <RouterContext>");
  }
  let t0;
  let t1;
  if ($[0] !== router) {
    t1 = {
      router,
      getQuery(key) {
        return getQuery(router, key);
      },
      pushRoute: (route, options) => pushRoute(router, route, options),
      replaceRoute: (route_0, options_0) => replaceRoute(router, route_0, options_0),
      createLink: (route_1, options_1) => createLink(router, route_1, options_1),
      createHref: route_2 => routeToString(router, route_2),
      createClickHandler: (route_3, options_2) => createClickHandler(router, route_3, options_2),
      isRouteActive: route_4 => router.route.startsWith(route_4.pathname)
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  t0 = t1;
  return t0;
}

/**
 * Get a URL param or query value.
 * @param key Query param name
 */
export function useQuery(key) {
  const $ = _c(3);
  const {
    router
  } = useRouter();
  let t0;
  if ($[0] !== key || $[1] !== router) {
    t0 = getQuery(router, key);
    $[0] = key;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Get a route parameter. This will throw if the route parameter doesn't exist.
 * @param key
 */
export function useRouteParam(key) {
  const $ = _c(3);
  const {
    router
  } = useRouter();
  const params = findParams(router.route);
  if (!params.includes(key)) {
    throw new Error(`The current page route does not contain the parameter "${key}". Did you mispell it?`);
  }
  let t0;
  if ($[0] !== key || $[1] !== router) {
    t0 = getQuery(router, key);
    $[0] = key;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const value = t0;
  if (!value) {
    throw new Error(`Route parameter "${key}" doesn’t exist`);
  }
  return value;
}

/**
 * Prefetch the JS bundles for a route
 * @param route
 */
export function usePrefetch(route) {
  const $ = _c(5);
  const {
    router
  } = useRouter();
  let t0;
  if ($[0] !== route || $[1] !== router) {
    t0 = () => {
      if (route) {
        router.prefetch(route.pathname);
      }
    };
    $[0] = route;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = route && route.pathname;
  let t2;
  if ($[3] !== t1) {
    t2 = [t1];
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t0, t2);
}